import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  Card,
  CardContent,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Collapse,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Edit,
  Visibility,
  Block,
  Refresh,
  Download,
  AttachMoney,
  Schedule,
  Cancel,
  CheckCircle,
  Error,
  Warning,
  ExpandMore,
  ExpandLess,
  QrCode,
  Email,
  Sms,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { voucherService } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import NotificationSnackbar, { NotificationType } from '../components/NotificationSnackbar';

interface VoucherFilter {
  status?: string;
  merchantId?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  amountMin?: number;
  amountMax?: number;
  currency?: string;
  voucherNumber?: string;
  customerEmail?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

interface CopyButtonProps {
  text: string;
  label?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label || 'to clipboard'}`}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          ml: 0.5,
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {copied ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const VoucherManagement: React.FC = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedVoucher, setSelectedVoucher] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [selectedVouchers, setSelectedVouchers] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState<VoucherFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: NotificationType;
  }>({ open: false, message: '', type: 'success' });

  const showNotification = (message: string, type: NotificationType = 'success') => {
    setNotification({ open: true, message, type });
  };

  // Maps the quick-filter tab index to the backend `filter` query param (BUG004).
  // 0 = All, 1 = Requires Action, 2 = Expiring Soon, 3 = Recent Activity.
  const TAB_FILTERS = ['', 'requires-action', 'expires-soon', 'recently-active'];

  useEffect(() => {
    fetchVouchers();
  }, [page, rowsPerPage, filters, tabValue]);

  const fetchVouchers = async () => {
    setLoading(true);
    try {
      const response = await voucherService.searchVouchers({
        search: filters.voucherNumber || '',
        page,
        size: rowsPerPage,
        filter: TAB_FILTERS[tabValue] || undefined,
      });

      // Apply client-side filters
      let filteredVouchers = response.content;

      if (filters.status) {
        filteredVouchers = filteredVouchers.filter(v => v.voucherStatus === filters.status);
      }

      if (filters.merchantId) {
        filteredVouchers = filteredVouchers.filter(v =>
          v.merchantId?.toLowerCase().includes(filters.merchantId!.toLowerCase())
        );
      }

      if (filters.currency) {
        filteredVouchers = filteredVouchers.filter(v => v.currency === filters.currency);
      }

      if (filters.amountMin !== undefined && filters.amountMin !== null) {
        filteredVouchers = filteredVouchers.filter(v => v.amount >= filters.amountMin!);
      }

      if (filters.amountMax !== undefined && filters.amountMax !== null) {
        filteredVouchers = filteredVouchers.filter(v => v.amount <= filters.amountMax!);
      }

      if (filters.dateFrom) {
        filteredVouchers = filteredVouchers.filter(v =>
          v.dateCreated && new Date(v.dateCreated) >= filters.dateFrom!
        );
      }

      if (filters.dateTo) {
        filteredVouchers = filteredVouchers.filter(v =>
          v.dateCreated && new Date(v.dateCreated) <= filters.dateTo!
        );
      }

      setVouchers(filteredVouchers);
      // Pagination must reflect the server's total for the active quick-filter/search, not the
      // size of the current page (BUG004). The optional client-side refinements above only narrow
      // the visible page; when none are active this is the true server total.
      const hasClientFilters = Boolean(
        filters.status || filters.merchantId || filters.currency ||
        filters.amountMin != null || filters.amountMax != null ||
        filters.dateFrom || filters.dateTo
      );
      setTotalCount(hasClientFilters ? filteredVouchers.length : (response.totalElements ?? filteredVouchers.length));
      setError(null);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, 'Failed to fetch vouchers');
      setError(errorMessage);
      showNotification(errorMessage, 'error');
      console.error('Error fetching vouchers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof VoucherFilter, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleViewDetails = (voucher: any) => {
    setSelectedVoucher(voucher);
    setDetailsOpen(true);
  };

  const handleCancelVoucher = async (voucherNumber: string) => {
    if (window.confirm('Are you sure you want to cancel this voucher?')) {
      try {
        // await voucherService.cancelVoucher(voucherNumber);
        fetchVouchers();
        showNotification('Voucher cancelled successfully', 'success');
      } catch (err) {
        console.error('Failed to cancel voucher:', err);
        const errorMessage = getErrorMessage(err, 'Failed to cancel voucher. Please try again.');
        showNotification(errorMessage, 'error');
      }
    }
  };

  const handleRefundVoucher = async (voucherNumber: string) => {
    if (window.confirm('Are you sure you want to refund this voucher? This action requires approval.')) {
      try {
        // await voucherService.requestRefund(voucherNumber);
        showNotification('Refund request submitted for approval', 'success');
        fetchVouchers();
      } catch (err) {
        console.error('Failed to request refund:', err);
        const errorMessage = getErrorMessage(err, 'Failed to request refund. Please try again.');
        showNotification(errorMessage, 'error');
      }
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedVouchers.length === 0) {
      alert('Please select vouchers first');
      return;
    }

    try {
      // Perform bulk action
      // await voucherService.bulkAction(action, selectedVouchers);
      setBulkActionDialog(false);
      setSelectedVouchers([]);
      fetchVouchers();
      showNotification(`Bulk action "${action}" completed successfully`, 'success');
    } catch (err) {
      console.error('Bulk action failed:', err);
      const errorMessage = getErrorMessage(err, 'Bulk action failed. Please try again.');
      showNotification(errorMessage, 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'AWAITING_PAYMENT': return 'primary';
      case 'CUSTOMER_PAID': return 'success';
      case 'AWAITING_RETAILER_SETTLEMENT': return 'info';
      case 'AWAITING_MERCHANT_SETTLEMENT': return 'info';
      case 'SETTLED': return 'success';
      case 'EXPIRED': return 'warning';
      case 'REFUNDED': return 'info';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'AWAITING_PAYMENT': return <Schedule />;
      case 'CUSTOMER_PAID': return <CheckCircle />;
      case 'AWAITING_RETAILER_SETTLEMENT': return <AttachMoney />;
      case 'AWAITING_MERCHANT_SETTLEMENT': return <AttachMoney />;
      case 'SETTLED': return <CheckCircle />;
      case 'EXPIRED': return <Warning />;
      case 'REFUNDED': return <AttachMoney />;
      case 'CANCELLED': return <Cancel />;
      default: return null;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Voucher Management</Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => {/* Export vouchers */}}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchVouchers}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Awaiting Payment
                </Typography>
                <Typography variant="h5">
                  {vouchers.filter(v => v.voucherStatus === 'AWAITING_PAYMENT').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Value
                </Typography>
                <Typography variant="h5">
                  {vouchers.length > 0 && vouchers[0]?.currency} {vouchers
                    .reduce((sum, v) => sum + v.amount, 0)
                    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Expired
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {vouchers.filter(v => v.voucherStatus === 'EXPIRED').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Refunded/Cancelled
                </Typography>
                <Typography variant="h5" color="error.main">
                  {vouchers.filter(v => v.voucherStatus === 'REFUNDED' || v.voucherStatus === 'CANCELLED').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters Section */}
        <Paper sx={{ mb: 3 }}>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Filters</Typography>
              <IconButton onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={showFilters}>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Voucher Number"
                    value={filters.voucherNumber || ''}
                    onChange={(e) => handleFilterChange('voucherNumber', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status || ''}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="AWAITING_PAYMENT">Awaiting Payment</MenuItem>
                      <MenuItem value="CUSTOMER_PAID">Customer Paid</MenuItem>
                      <MenuItem value="AWAITING_RETAILER_SETTLEMENT">Awaiting Retailer Settlement</MenuItem>
                      <MenuItem value="AWAITING_MERCHANT_SETTLEMENT">Awaiting Merchant Settlement</MenuItem>
                      <MenuItem value="SETTLED">Settled</MenuItem>
                      <MenuItem value="EXPIRED">Expired</MenuItem>
                      <MenuItem value="REFUNDED">Refunded</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="From Date"
                    value={filters.dateFrom || null}
                    onChange={(date) => handleFilterChange('dateFrom', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="To Date"
                    value={filters.dateTo || null}
                    onChange={(date) => handleFilterChange('dateTo', date)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={filters.currency || ''}
                      label="Currency"
                      onChange={(e) => handleFilterChange('currency', e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="USD">USD</MenuItem>
                      <MenuItem value="ZWG">ZWG</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Min Amount"
                    type="number"
                    value={filters.amountMin || ''}
                    onChange={(e) => handleFilterChange('amountMin', Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Max Amount"
                    type="number"
                    value={filters.amountMax || ''}
                    onChange={(e) => handleFilterChange('amountMax', Number(e.target.value))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box display="flex" gap={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={fetchVouchers}
                      startIcon={<Search />}
                    >
                      Search
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={clearFilters}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        </Paper>

        {/* Tabs for different views */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); setPage(0); }}>
            <Tab label="All Vouchers" />
            <Tab label="Requires Action" />
            <Tab label="Expiring Soon" />
            <Tab label="Recent Activity" />
          </Tabs>
        </Paper>

        {/* Vouchers Table */}
        <Paper>
          {loading && <LinearProgress />}
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Voucher Number</TableCell>
                  <TableCell>Merchant</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher) => (
                  <TableRow
                    key={voucher.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/voucher/${voucher.voucherNumber}`)}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <QrCode fontSize="small" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {voucher.voucherNumber}
                        </Typography>
                        <CopyButton text={voucher.voucherNumber} label="voucher number" />
                      </Box>
                    </TableCell>
                    <TableCell>{voucher.merchantId}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{voucher.productDescription}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {voucher.currency} {voucher.amount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(voucher.voucherStatus)}
                        color={getStatusColor(voucher.voucherStatus) as any}
                        size="small"
                        icon={getStatusIcon(voucher.voucherStatus) as any}
                      />
                    </TableCell>
                    <TableCell>
                      {voucher.dateCreated && format(parseISO(voucher.dateCreated), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={
                          new Date(voucher.expiresAt) < new Date()
                            ? 'error'
                            : 'textPrimary'
                        }
                      >
                        {format(parseISO(voucher.expiresAt), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(voucher)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {voucher.voucherStatus === 'AWAITING_PAYMENT' && (
                          <>
                            <Tooltip title="Send Reminder">
                              <IconButton size="small">
                                <Email fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Voucher">
                              <IconButton
                                size="small"
                                onClick={() => handleCancelVoucher(voucher.voucherNumber)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {voucher.voucherStatus === 'CUSTOMER_PAID' && (
                          <Tooltip title="Request Refund">
                            <IconButton
                              size="small"
                              onClick={() => handleRefundVoucher(voucher.voucherNumber)}
                            >
                              <AttachMoney fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Voucher Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Voucher Details
            {selectedVoucher && (
              <Chip
                label={getStatusLabel(selectedVoucher.voucherStatus)}
                color={getStatusColor(selectedVoucher.voucherStatus) as any}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </DialogTitle>
          <DialogContent>
            {selectedVoucher && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Voucher Number
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedVoucher.voucherNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedVoucher.currency} {selectedVoucher.amount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Merchant ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedVoucher.merchantId}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Product
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedVoucher.productDescription}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Created Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedVoucher.dateCreated && format(parseISO(selectedVoucher.dateCreated), 'PPpp')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">
                    Expiry Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {format(parseISO(selectedVoucher.expiresAt), 'PPpp')}
                  </Typography>
                </Grid>
                {selectedVoucher.paidAt && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        Payment Date
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {format(parseISO(selectedVoucher.paidAt), 'PPpp')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        Retailer ID
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedVoucher.retailerId}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
            {selectedVoucher?.voucherStatus === 'AWAITING_PAYMENT' && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() => {/* Send reminder */}}
                >
                  Send Reminder
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleCancelVoucher(selectedVoucher.voucherNumber)}
                >
                  Cancel Voucher
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification({ ...notification, open: false })}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default VoucherManagement;